import { useState, useEffect } from 'react';
import {
  Save,
  AlertTriangle,
  CheckCircle,
  Info,
  Clock,
  Users,
  Shield,
} from 'lucide-react';
import {
  tenantSettingsService,
  type TenantSettings,
  type DeadlineUnit,
} from '@/services/tenantSettingsService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { Permission } from '@/types/global.types';
import { ShieldOff } from 'lucide-react';

const DEADLINE_UNIT_OPTIONS: { value: DeadlineUnit; label: string }[] = [
  { value: 'DAY', label: 'Jour(s)' },
  { value: 'WEEK', label: 'Semaine(s)' },
  { value: 'MONTH', label: 'Mois' },
];

export default function UserSettingsPage() {
  const { hasPermission } = useAuthStore();
  const canView = hasPermission(Permission.SETTINGS_USERS_VIEW);
  const canManage = hasPermission(Permission.SETTINGS_USERS_MANAGE);

  const [settings, setSettings] = useState<Partial<TenantSettings>>({
    profileSubmissionDeadlineValue: 4,
    profileSubmissionDeadlineUnit: 'WEEK',
    profileDeadlineEnforced: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await tenantSettingsService.getSettings();
      setSettings(data);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement des paramètres';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof TenantSettings, value: unknown) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await tenantSettingsService.updateDeadlineSettings({
        profileSubmissionDeadlineValue: settings.profileSubmissionDeadlineValue,
        profileSubmissionDeadlineUnit: settings.profileSubmissionDeadlineUnit,
        profileDeadlineEnforced: settings.profileDeadlineEnforced,
      });
      setSuccess(true);
      setHasChanges(false);
      setError(null);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  // Calculate human-readable deadline description
  const getDeadlineDescription = () => {
    const value = settings.profileSubmissionDeadlineValue || 4;
    const unit = settings.profileSubmissionDeadlineUnit || 'WEEK';

    const unitLabels: Record<DeadlineUnit, { singular: string; plural: string }> = {
      DAY: { singular: 'jour', plural: 'jours' },
      WEEK: { singular: 'semaine', plural: 'semaines' },
      MONTH: { singular: 'mois', plural: 'mois' },
    };

    const label = value === 1 ? unitLabels[unit].singular : unitLabels[unit].plural;
    return `${value} ${label}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Access denial if user doesn't have view permission
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="p-4 bg-red-100 rounded-full mb-4">
          <ShieldOff className="w-12 h-12 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès Refusé</h2>
        <p className="text-gray-500 text-center max-w-md">
          Vous n'avez pas la permission d'accéder aux paramètres des utilisateurs.
          Veuillez contacter votre administrateur si vous pensez qu'il s'agit d'une erreur.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres des Utilisateurs</h1>
        <p className="text-gray-500 mt-1">
          Configurez les paramètres de délai de soumission de profil et autres options utilisateurs
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Succès</p>
            <p className="text-sm text-green-600">Les paramètres ont été sauvegardés avec succès.</p>
          </div>
        </div>
      )}

      {/* Profile Deadline Settings */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Délai de Soumission de Profil</h2>
            <p className="text-sm text-gray-500">
              Configurez le délai accordé aux nouveaux utilisateurs pour compléter leur profil
            </p>
          </div>
        </div>

        {/* Enforcement Toggle */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <label className={`flex items-center gap-3 ${canManage ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}`}>
            <input
              type="checkbox"
              checked={settings.profileDeadlineEnforced || false}
              onChange={(e) => handleChange('profileDeadlineEnforced', e.target.checked)}
              disabled={!canManage}
              className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500 disabled:opacity-50"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-600" />
                Activer le délai de soumission de profil
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Lorsque activé, les utilisateurs qui n'ont pas soumis leur profil à temps auront un accès restreint à la plateforme
              </p>
            </div>
          </label>
        </div>

        {settings.profileDeadlineEnforced && (
          <>
            {/* Deadline Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durée du délai
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={settings.profileSubmissionDeadlineValue || 4}
                    onChange={(e) => handleChange('profileSubmissionDeadlineValue', parseInt(e.target.value) || 1)}
                    disabled={!canManage}
                    className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <select
                    value={settings.profileSubmissionDeadlineUnit || 'WEEK'}
                    onChange={(e) => handleChange('profileSubmissionDeadlineUnit', e.target.value as DeadlineUnit)}
                    disabled={!canManage}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {DEADLINE_UNIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Temps accordé à partir de la première connexion
                </p>
              </div>

              <div className="flex items-center">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 w-full">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">Délai actuel: {getDeadlineDescription()}</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Les nouveaux utilisateurs auront {getDeadlineDescription()} pour compléter et soumettre leur profil
                  </p>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Comment ça fonctionne
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <span className="font-medium text-blue-800">Première connexion</span>
                  </div>
                  <p className="text-xs text-blue-700">
                    Quand un utilisateur se connecte pour la première fois, le compte à rebours commence
                  </p>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <span className="font-medium text-amber-800">Période de grâce</span>
                  </div>
                  <p className="text-xs text-amber-700">
                    L'utilisateur a accès complet mais voit un rappel du délai restant
                  </p>
                </div>

                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <span className="font-medium text-red-800">Après le délai</span>
                  </div>
                  <p className="text-xs text-red-700">
                    Accès restreint - l'utilisateur ne peut accéder qu'à la page de profil
                  </p>
                </div>
              </div>
            </div>

            {/* Rejection info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">En cas de rejet du profil</h4>
              <p className="text-xs text-gray-600">
                Si le profil d'un utilisateur est rejeté après validation, le délai redémarre à partir de la date de rejet,
                donnant à l'utilisateur une nouvelle période de grâce pour corriger et resoumettre son profil.
              </p>
            </div>
          </>
        )}

        {!settings.profileDeadlineEnforced && (
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              Le délai de soumission de profil est actuellement désactivé. Les utilisateurs peuvent accéder à toutes les fonctionnalités sans avoir à compléter leur profil dans un délai imparti.
            </p>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-blue-800">Information</p>
          <p className="text-sm text-blue-600">
            Ces paramètres s'appliquent à tous les nouveaux étudiants et membres du personnel qui doivent soumettre leur profil.
            Les administrateurs, directeurs et parents ne sont pas soumis à ce délai.
          </p>
        </div>
      </div>

      {/* Save button */}
      {canManage ? (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${
              hasChanges
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Sauvegarder les paramètres
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="flex justify-end">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm">
            <Shield className="w-4 h-4" />
            <span>Mode lecture seule - Permission de modification requise</span>
          </div>
        </div>
      )}
    </div>
  );
}
